import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { normalizeNetworkName } from '~publish/helpers/channels'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useCommunityConfig } from '../../CommunityConfig'
import {
  useMentionData,
  useMentionReactionError,
} from '../../Mentions/context/MentionContext'
import type { ReactionType } from '../../hooks/configuration/useConfigurationQuery'
import { useReactToMention } from '../../hooks/engagement/useReactToMention'
import { MultiReactions } from '../APIReaction/MultiReactions'
import { ReactionIcon } from '../APIReaction/ReactionIcon'
import { ReconnectToLikeDialog } from '../ChannelRefresh/ReconnectToLikeDialog'
import { isChannelIconService } from '../../util/isChannelIconService'

import styles from './MentionReactions.module.css'

export const MentionReactions_Channel = graphql(/* GraphQL */ `
  fragment MentionReactions_Channel on Channel @_unmask {
    id
    name
    displayName
    avatar
    apiVersion
    service
    allowedActions
  }
`)

export const MentionReactions_Mention = graphql(/* GraphQL */ `
  fragment MentionReactions_Mention on Mention @_unmask {
    id
    reactions {
      type
    }
  }
`)

type MentionReactionsProps = {
  channel: FragmentOf<typeof MentionReactions_Channel>
}

const VIEW_ONLY_TOOLTIP =
  'Ask an admin to update your permissions to interact with this mention.'

/**
 * Thrown to roll the optimistic state back when the channel must reconnect
 * before it can react. The reconnect dialog explains itself, so this carries
 * no user-facing copy.
 */
class ReactionAuthorizationRequired extends Error {
  constructor() {
    super('The channel needs to reconnect before it can react')
    this.name = 'ReactionAuthorizationRequired'
  }
}

/**
 * Reaction control for a mention, rendered in the `MentionHeader`'s trailing
 * slot. Renders only when the channel's configuration lists a reaction for the
 * mention engagement. The state is optimistic and rolls back if the mutation
 * fails.
 *
 * The configuration decides the shape: more than one reaction gets the
 * `MultiReactions` picker (LinkedIn offers six), a single one gets a plain
 * like icon resting on the configured icon.
 */
export const MentionReactions = ({
  channel,
}: MentionReactionsProps): JSX.Element | null => {
  const { config } = useCommunityConfig()
  const mention = useMentionData()
  const { reactToMention, loading } = useReactToMention()
  const { setReactionError } = useMentionReactionError()

  const isLikedFromData = (mention.reactions?.length ?? 0) > 0
  const isMutatingRef = useRef(false)

  const [optimisticIsReacted, setOptimisticIsReacted] =
    useState(isLikedFromData)
  const [isReconnectOpen, setIsReconnectOpen] = useState(false)

  // Re-sync optimistic state from data once a mutation settles (the cache
  // update lands), but never mid-flight — that would clobber the optimistic
  // value with the pre-mutation one.
  useEffect(() => {
    if (!isMutatingRef.current) {
      setOptimisticIsReacted(isLikedFromData)
    }
  }, [isLikedFromData])

  // The configuration says which reactions the channel offers for a mention and
  // which icon to rest on — LinkedIn rests on a thumb, X on a heart. None means
  // the network cannot be reacted to through Buffer, so no control renders.
  const reactions = config.getEngagementReactions(channel.id, 'mention')

  // An `external` reaction sends the person to the network to react, so Buffer
  // applies none. Networks that offer nothing else get no control; the mention
  // is still reachable through the header's "open in a new tab" action.
  // Matches `CommentReactions`.
  const applicableReactions =
    reactions?.supportedReactions.filter(
      (reaction) => reaction.reactionType !== 'external',
    ) ?? []

  if (!reactions || applicableReactions.length === 0) {
    return null
  }

  const channelService = isChannelIconService(channel.service)
    ? channel.service
    : null
  if (!channelService) {
    return null
  }

  const supportsMultiReactions = applicableReactions.length > 1

  // View-only actors still see the existing reaction state — the icon renders
  // disabled with an explanatory tooltip. Community uses `manageComments` as
  // its single engagement-management permission (there's no `manageMentions`).
  const canManage = canPerform(channel, 'manageComments')

  const needsAuthorization = config.reactionsNeedAuthorization(
    channel.id,
    'mention',
  )

  /**
   * Applies the reaction and owns the mention-side error copy. Rethrows so the
   * caller rolls its optimistic state back.
   */
  const applyReaction = async (
    reactionType: ReactionType,
    isActive: boolean,
  ): Promise<void> => {
    setReactionError(null)

    // Liking needs an OAuth scope this channel does not hold. Ask for a
    // reconnect rather than firing a mutation the network will reject. The
    // dialog is the feedback here, so no error copy goes with the throw.
    if (needsAuthorization) {
      setIsReconnectOpen(true)
      throw new ReactionAuthorizationRequired()
    }

    try {
      await reactToMention(mention.id, isActive, reactionType)
    } catch (error) {
      // The API names what went wrong — a deleted mention, a revoked scope — so
      // pass its message through. Only a non-Error needs the generic line.
      setReactionError(
        error instanceof Error
          ? error.message
          : 'An error occurred while reacting to the mention. Please try again.',
      )

      throw error
    }
  }

  const handleClick = async (e: React.MouseEvent): Promise<void> => {
    if (isMutatingRef.current) return
    e.stopPropagation()

    const newLikedState = !optimisticIsReacted
    isMutatingRef.current = true
    setOptimisticIsReacted(newLikedState)

    try {
      await applyReaction('like', newLikedState)
    } catch {
      setOptimisticIsReacted(!newLikedState)
    } finally {
      isMutatingRef.current = false
    }
  }

  return (
    <>
      {supportsMultiReactions ? (
        <MultiReactions
          likingIcon={reactions.restingIcon}
          supportedReactions={applicableReactions}
          isReacted={isLikedFromData}
          currentReactionType={mention.reactions?.[0]?.type}
          loading={loading}
          onReact={applyReaction}
          entity="mention"
          service={channelService}
          networkName={normalizeNetworkName(channel.service)}
          disabled={!canManage}
          viewOnlyTooltip={VIEW_ONLY_TOOLTIP}
        />
      ) : (
        <div data-mention-reaction className={styles.container}>
          <ReactionIcon
            likingIcon={reactions.restingIcon}
            entity="mention"
            isLiked={optimisticIsReacted}
            loading={loading}
            showTooltip
            disabled={!canManage}
            tooltipOverride={!canManage ? VIEW_ONLY_TOOLTIP : undefined}
            onClick={(e): void => {
              void handleClick(e)
            }}
          />
        </div>
      )}
      <ReconnectToLikeDialog
        channel={channel}
        open={isReconnectOpen}
        onOpenChange={setIsReconnectOpen}
      />
    </>
  )
}

MentionReactions.displayName = 'MentionReactions'
